// Meshy生成GLBのスキン品質を、外部ライブラリなしで保存前に検査する。
// ブラウザ完結版でも使えるようUint8Array / DataView / TextDecoderだけで実装する。

const GLB_MAGIC = 0x46546c67;
const JSON_CHUNK = 0x4e4f534a;
const BIN_CHUNK = 0x004e4942;
const COMPONENTS = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT2: 4, MAT3: 9, MAT4: 16 };
const COMPONENT_BYTES = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 };

function parseGlb(bytes) {
    const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    if (data.byteLength < 20) throw new Error('GLBが短すぎます');
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    if (view.getUint32(0, true) !== GLB_MAGIC || view.getUint32(4, true) !== 2) {
        throw new Error('GLB 2.0形式ではありません');
    }
    let offset = 12;
    let json = null;
    let bin = null;
    while (offset + 8 <= data.byteLength) {
        const length = view.getUint32(offset, true);
        const type = view.getUint32(offset + 4, true);
        const start = offset + 8;
        const end = start + length;
        if (end > data.byteLength) throw new Error('GLBチャンク長が不正です');
        if (type === JSON_CHUNK) {
            const text = new TextDecoder().decode(data.subarray(start, end)).replace(/\u0000+$/g, '').trim();
            json = JSON.parse(text);
        } else if (type === BIN_CHUNK) {
            bin = data.subarray(start, end);
        }
        offset = end;
    }
    if (!json || !bin) throw new Error('GLBのJSON/BINチャンクがありません');
    return { json, bin };
}

function readRawComponent(view, offset, componentType) {
    switch (componentType) {
        case 5120: return view.getInt8(offset);
        case 5121: return view.getUint8(offset);
        case 5122: return view.getInt16(offset, true);
        case 5123: return view.getUint16(offset, true);
        case 5125: return view.getUint32(offset, true);
        case 5126: return view.getFloat32(offset, true);
        default: throw new Error(`未対応のcomponentTypeです: ${componentType}`);
    }
}

function normalizeComponent(value, componentType) {
    switch (componentType) {
        case 5120: return Math.max(value / 127, -1);
        case 5121: return value / 255;
        case 5122: return Math.max(value / 32767, -1);
        case 5123: return value / 65535;
        case 5125: return value / 4294967295;
        default: return value;
    }
}

function accessorReader(gltf, bin, accessorIndex) {
    const accessor = gltf.accessors?.[accessorIndex];
    if (!accessor || accessor.sparse) throw new Error('スキン属性のアクセサを読み取れません');
    const bufferView = gltf.bufferViews?.[accessor.bufferView];
    if (!bufferView || (bufferView.buffer ?? 0) !== 0) throw new Error('スキン属性のbufferViewを読み取れません');
    const components = COMPONENTS[accessor.type];
    const bytesPerComponent = COMPONENT_BYTES[accessor.componentType];
    if (!components || !bytesPerComponent) throw new Error('スキン属性の型が不正です');
    const elementBytes = components * bytesPerComponent;
    const stride = bufferView.byteStride ?? elementBytes;
    const base = (bufferView.byteOffset ?? 0) + (accessor.byteOffset ?? 0);
    if (base < 0 || base + Math.max(0, accessor.count - 1) * stride + elementBytes > bin.byteLength) {
        throw new Error('スキン属性がBINチャンク範囲外です');
    }
    const view = new DataView(bin.buffer, bin.byteOffset, bin.byteLength);
    return {
        count: accessor.count,
        components,
        get(index, component) {
            const raw = readRawComponent(view, base + index * stride + component * bytesPerComponent, accessor.componentType);
            return accessor.normalized ? normalizeComponent(raw, accessor.componentType) : raw;
        },
    };
}

/**
 * @returns {{ok:boolean, jointCount:number, usedJointCount:number, totalVertexCount:number,
 *   weightedVertexCount:number, requiredWeightedJoints:number, reason:string|null}}
 */
export function inspectGlbSkinning(bytes, config = {}) {
    try {
        const { json: gltf, bin } = parseGlb(bytes);
        const jointCount = Math.max(0, ...(gltf.skins ?? []).map((skin) => skin.joints?.length ?? 0));
        if (jointCount === 0) throw new Error('スキンまたはジョイントがありません');
        const minJoints = Number.isFinite(Number(config.minWeightedJoints)) ? Number(config.minWeightedJoints) : 12;
        const minRatio = Number.isFinite(Number(config.minWeightedJointRatio)) ? Number(config.minWeightedJointRatio) : 0.5;
        const requiredWeightedJoints = Math.min(jointCount, Math.max(1, minJoints, Math.ceil(jointCount * minRatio)));
        const usedJoints = new Set();
        let totalVertexCount = 0;
        let weightedVertexCount = 0;
        let primitiveCount = 0;
        for (const mesh of gltf.meshes ?? []) {
            for (const primitive of mesh.primitives ?? []) {
                const jointAccessor = primitive.attributes?.JOINTS_0;
                const weightAccessor = primitive.attributes?.WEIGHTS_0;
                if (jointAccessor == null || weightAccessor == null) continue;
                const joints = accessorReader(gltf, bin, jointAccessor);
                const weights = accessorReader(gltf, bin, weightAccessor);
                if (joints.components !== 4 || weights.components !== 4 || joints.count !== weights.count) {
                    throw new Error('JOINTS_0/WEIGHTS_0の形式が不正です');
                }
                primitiveCount++;
                totalVertexCount += joints.count;
                for (let vertex = 0; vertex < joints.count; vertex++) {
                    let weightSum = 0;
                    for (let component = 0; component < 4; component++) {
                        const weight = weights.get(vertex, component);
                        weightSum += weight;
                        if (weight > 1e-5) {
                            const joint = joints.get(vertex, component);
                            if (Number.isInteger(joint) && joint >= 0 && joint < jointCount) usedJoints.add(joint);
                        }
                    }
                    if (weightSum > 0.99 && weightSum < 1.01) weightedVertexCount++;
                }
            }
        }
        if (primitiveCount === 0 || totalVertexCount === 0) throw new Error('JOINTS_0/WEIGHTS_0を持つメッシュがありません');
        const usedJointCount = usedJoints.size;
        const allVerticesWeighted = weightedVertexCount === totalVertexCount;
        const enoughJoints = usedJointCount >= requiredWeightedJoints;
        return {
            ok: allVerticesWeighted && enoughJoints,
            jointCount,
            usedJointCount,
            totalVertexCount,
            weightedVertexCount,
            requiredWeightedJoints,
            reason: !allVerticesWeighted
                ? `正常なウェイト合計を持つ頂点が ${weightedVertexCount}/${totalVertexCount} です`
                : !enoughJoints
                    ? `ウェイト使用ボーンが ${usedJointCount}/${jointCount}本です（必要 ${requiredWeightedJoints}本以上）`
                    : null,
        };
    } catch (error) {
        return {
            ok: false,
            jointCount: 0,
            usedJointCount: 0,
            totalVertexCount: 0,
            weightedVertexCount: 0,
            requiredWeightedJoints: 0,
            reason: error?.message ?? String(error),
        };
    }
}
